import moment from 'moment'
import {
  isEmpty,
  trim,
  every,
  first,
  flow,
  get,
  isEqual,
  isInteger,
  isMatch,
  isNil,
  last,
  map,
  negate,
  overEvery,
  overSome,
  pick,
  props,
  set,
  size,
  some,
  take,
  toFinite,
  toNumber,
  values,
  includes,
  concat,
  compact,
  inRange,
} from 'lodash/fp'
import VALUES from 'core/data-model/constants/values'
import { createChecker } from 'core/service/lib/check'

import { isCordova } from 'e-submission/utils'
import { createValidation, createValidator } from 'e-submission/utils/validator'
import {
  isInteger as isDigit,
  hasActiveLicense,
  hasActiveOrInapplicableLicense,
  hasAnyActiveLicense,
  isBetweenEqual,
  isDecimal,
  isEmail,
  isFutureDate,
  isIDCardNo,
  isLengthEqualTo,
  isLengthLessThanEqual,
  isMoreThan,
  isNotEmpty,
  isPersonName,
  isPersonNameEN,
  isTextAndNumberEN,
  isPassport,
} from 'e-submission/utils/pattern'
import {
  isEqualBeneficiaryAllocation,
  isExceedBeneficiaryAllocation,
  isInsuredAndPayerCitizenIdEqual,
  isInsuredAndPayerPassportEqual,
  isJuvenile,
  isKid,
  isMinFirstTotalPayment,
  getQuickQuoteProductCategory,
  isAdditionalDocument,
  getQuickQuoteTotalPremium,
  isMaxFirstTotalPayment,
  isQuickQuoteModelFactorMonthly,
  getMaximumPaidPremium,
  getMinimumPaidPremium,
  isValidateTaxId,
  isInsuredIdCardZeroByte,
  isPayerIdCardZeroByte,
  isBancSuitablityFormZeroByte,
  getCertifyByAgent,
  getQuickQuoteSelectedProductLicenses,
  getSelectedRiders,
  getQuickQuoteProductCode,
  getQuickQuoteBasicPlanCode,
  isConditionRelationJuvinile,
  isConditionRelationParants,
  isInsureSamePayer,
  isATPRequireBankNumber,
} from './selectors'
import { getRiderLicenses, getBankAccountFormat, calculateBMI } from './utils'
import { PAYMENT_TYPE } from 'e-submission/domain/data-model/constants'
import { formatNumber } from 'core/service/lib/number-format'
import { getToggles } from 'quick-quote/feature-toggles'
import { AMLO_RIGHT_TYPE_CODES } from 'identity/amlo'
import _ from 'lodash'

const isUploaded = every(['checked', true])

const getUploadError = (value, { state }) => {
  const pickIn = (obj) => (keys) => pick(keys, obj)
  return flow(map('name'), pickIn(state.ui.errors), values, first)(value)
}

const hasUploadError = flow(getUploadError, negate(isNil))

const getReceiptKey = (fieldId) => {
  const key = fieldId.match(/^receipts\[[0-9]+\]/)
  return key ? key[0] : 'receipts[0]'
}

export const validateSendLinkInputPopup = createChecker([
  [
    'emptyValue',
    {
      type: 'VALIDATION',
      rule: ({ textInput }) => isNotEmpty(textInput),
      message: 'กรุณากรอกข้อมูล',
    },
  ],
  [
    'invalidInput',
    {
      type: 'VALIDATION',
      rule: ({ textInput }, method) => {
        if (textInput) return method === 'email' ? isEmail(textInput) : isLengthEqualTo(10)(textInput)
        return true
      },
      message: ({ }, method) =>
        method === 'email' ? 'รูปแบบอีเมลไม่ถูกต้อง กรุณาลองใหม่อีกครั้ง' : 'กรุณากรอกข้อมูลให้ถูกต้อง',
    },
  ],
])

const RECOMMENDER_MSG = {
  AMLO: 'กรุณาระบุตัวแทนที่ผ่านการอบรม AML เรียบร้อยแล้ว',
  AGENT_STATUS_TERMINATE: 'กรุณาระบุตัวแทนที่ได้ใบอนุญาตและยังมีผลบังคับใช้',
  AGENT_LICENSE_FALLBACK: 'กรุณาตรวจสอบสิทธิ์การขายผลิตภัณฑ์ของตัวแทน',
  INVESTMENT_AGENT_FALLBACK: 'กรุณาเลือกตัวแทนที่มีใบอนุญาตให้บริการขายผลิตภัณฑ์เพื่อการลงทุน',
  INVESTMENT_LICENSE: 'กรุณาระบุตัวแทนที่มีใบอนุญาตในการเสนอขายผลิตภัณฑ์เพื่อการลงทุน',
  UDR_LICENSE: 'กรุณาระบุตัวแทนที่มีใบอนุญาตในการเสนอขายผลิตภัณฑ์ยูนิตลิงค์',
  S7CC: 'กรุณาระบุตัวแทนที่ได้รับสิทธิให้เสนอขายผลิตภัณฑ์ ซุปเปอร์ เซเว่น คริติคอลแคร์',
  GLOBAL_INDEX: 'กรุณาระบุตัวแทนที่ได้รับสิทธิ์ให้เสนอขายผลิตภัณฑ์ โกลบอล อินเด็กซ์ 16/6',
  INDEX_LINKED_25_5: 'กรุณาระบุตัวแทนที่ได้รับสิทธิ์ให้เสนอขายผลิตภัณฑ์ อินเด็กซ์ ลิงค์ 25/5',
  INDEX_LINKED_15_8: 'กรุณาระบุตัวแทนที่ได้รับสิทธิ์ให้เสนอขายผลิตภัณฑ์ อินเด็กซ์ ลิงค์ 15/8',
  EASY_PROTECT_6: 'กรุณาระบุตัวแทนที่ได้รับสิทธิ์ให้เสนอขายผลิตภัณฑ์ อีซี่ โพรเทค 6',
  LIFE_PROTECT_PLUS: 'กรุณาระบุตัวแทนที่ได้รับสิทธิ์ให้เสนอขายผลิตภัณฑ์ ไลฟ์ โพรเทค+',
  RIDERS: {
    IHEALTHY: 'กรุณาระบุตัวแทนที่ได้รับสิทธิให้เสนอขายผลิตภัณฑ์ ไอเฮลท์ตี้ อัลตร้า',
    MEX: 'กรุณาระบุตัวแทนที่ได้รับสิทธิให้เสนอขายผลิตภัณฑ์ ค่ารักษาพยาบาล เอ็กซ์ตร้า',
    UDR_HEALTH: 'กรุณาระบุตัวแทนที่ได้รับสิทธิให้เสนอขายผลิตภัณฑ์ สัญญาเพิ่มเติมเฮลท์ลิงค์, แคชลิงค์',
    CI_MED: 'กรุณาระบุตัวแทนที่ได้รับสิทธิ์ให้เสนอขายผลิตภัณฑ์ โรคร้ายโซชิลด์',
    CI123: 'กรุณาระบุตัวแทนที่ได้รับสิทธิ์ให้เสนอขายผลิตภัณฑ์ สัญญาเพิ่มเติมคุ้มครองโรคร้าย (CI 123)',
  },
}

const RIDER_LICENSE_CHECKS = [
  { license: 'IHEALTHY RIGHT', message: RECOMMENDER_MSG.RIDERS.IHEALTHY },
  { license: 'MEX RIGHT', message: RECOMMENDER_MSG.RIDERS.MEX },
  { license: 'UDR HEALTH', message: RECOMMENDER_MSG.RIDERS.UDR_HEALTH, requireProductCode: 'RPUDR' },
  { license: 'CI MED RIGHT', message: RECOMMENDER_MSG.RIDERS.CI_MED },
  { license: 'CI 123', message: RECOMMENDER_MSG.RIDERS.CI123 },
]

const getInvestmentLicenseError = (registrations, productCode) => {
  if (!hasActiveLicense('SINGLE LIC')(registrations)) return RECOMMENDER_MSG.INVESTMENT_LICENSE
  if (!hasActiveLicense('OIC REG')(registrations)) return RECOMMENDER_MSG.INVESTMENT_LICENSE
  if (!hasActiveLicense('ILP RIGHT')(registrations)) return RECOMMENDER_MSG.INVESTMENT_LICENSE
  if (productCode === 'RPUDR' && !hasActiveLicense('UDR RIGHT')(registrations)) return RECOMMENDER_MSG.UDR_LICENSE
  if (!hasActiveOrInapplicableLicense('IC REFRESHER')(registrations)) return RECOMMENDER_MSG.INVESTMENT_LICENSE
  return undefined
}

const getRiderLicenseError = (registrations, productCode, selectedRiders) => {
  const selectedRiderLicense = getRiderLicenses(selectedRiders) || []
  const matched = RIDER_LICENSE_CHECKS.find(({ license, requireProductCode }) => {
    if (requireProductCode && productCode !== requireProductCode) return false
    return includes(license, selectedRiderLicense) && !hasActiveLicense(license)(registrations)
  })
  return matched?.message
}

const getBasicProductLicenseError = (registrations, productCode) => {
  const checks = [
    { productCodes: VALUES.S7CC_PRODUCT_PLANCODE, license: '[CI] S7CC', message: RECOMMENDER_MSG.S7CC },
    {
      productCodes: Object.values(VALUES.GLOBAL_INDEX_16_6_CODE),
      license: 'GLOBAL INDEX',
      message: RECOMMENDER_MSG.GLOBAL_INDEX,
    },
    {
      productCodes: Object.values(VALUES.INDEX_LINKED_25_5_CODE),
      license: '25/5 RIGHT',
      message: RECOMMENDER_MSG.INDEX_LINKED_25_5,
    },
    {
      productCodes: Object.values(VALUES.INDEX_LINKED_15_8_CODE),
      license: '15/8 RIGHT',
      message: RECOMMENDER_MSG.INDEX_LINKED_15_8,
    },
    {
      productCodes: Object.values(VALUES.EASY_PROTECT_6_CODE),
      license: 'WL PROTECT 99/6',
      message: RECOMMENDER_MSG.EASY_PROTECT_6,
    },
    {
      productCodes: [
        ...Object.values(VALUES.LIFEPROTECT_PLUS_CODE),
        ...Object.keys(VALUES.HEALTH_ULTRA_LIFEPROTECT_PLUS_CODE),
        ...Object.keys(VALUES.HEALTH_EXTRA_LIFEPROTECT_PLUS_CODE)
      ],
      license: 'WL PROTECT+ 99',
      message: RECOMMENDER_MSG.LIFE_PROTECT_PLUS,
    },
  ]
  const matched = checks.find(
    ({ productCodes, license }) => includes(productCode, productCodes) && !hasActiveLicense(license)(registrations)
  )
  return matched?.message
}

export default createValidator({
  '.': createValidation({
    requiredMessages: [
      {
        // rule: isNotEmpty,
        rule: (value, { id, app }) => {
          const fieldName = id.replace('numberMoobanArkan', 'addressNo')

          const isFieldNumberMoobanArkan = id.includes('numberMoobanArkan')
          const addressNoData = _.get(app, fieldName) || ''

          if (isFieldNumberMoobanArkan && addressNoData) {
            return addressNoData === '-' ? isNotEmpty(value) : true
          }

          return isNotEmpty(value)
        },
        message: (value, { id, app }) => {
          if (getToggles().ENABLE_EKYC_F2F && id === 'validationEKYC.payer.isRequired') {
            return 'กรุณายืนยันตัวตนของผู้ชำระเบี้ย'
          } else if (getToggles().ENABLE_EKYC_F2F && id === 'validationEKYC.insured.isRequired') {
            return 'กรุณายืนยันตัวตนของผู้เอาประกันภัย'
          } else {
            return 'กรุณากรอกข้อมูล'
          }
        },
      },
    ],
  }),
  '(id-card|document|selfie|official-document|additionalDocument.add|Document|bankReceipt)$': createValidation({
    errorMessages: [
      {
        rule: negate(hasUploadError),
        message: (value, appState) => {
          const fileErrorType = getUploadError(value, appState)

          if (fileErrorType === 'INVALID_IMAGE_FILE') {
            return 'กรุณาแนบไฟล์รูปที่รองรับ เช่น .jpg หรือ .png หากท่านใช้ iPhone หรือ iPad ในการถ่ายรูป กรุณาตั้งค่าที่ การตั้งค่า (Setting) > กล้อง (Camera) > รูปแบบ (Format) และเลือก เข้ากันได้มากที่สุด (Most Compatible) และนำมาถ่ายรูปใหม่อีกครั้ง'
          }

          if (fileErrorType === 'FILE_SIZE_OVER_LIMIT') {
            return isCordova ? 'กรุณาลดขนาดรูปและลองถ่ายใหม่' : 'ขนาดรูปใหญ่เกินไป'
          } else {
            return isCordova ? 'กรุณาถ่ายรูปใหม่' : 'กรุณาแนบรูปใหม่'
          }
        },
      },
    ],
    requiredMessages: [
      {
        rule: overSome([
          isUploaded,
          hasUploadError,
          (value) => {
            const name = (value[0] && value[0].name) || ''
            return isAdditionalDocument(name)
          },
        ]),
        message: isCordova ? 'กรุณาถ่ายรูป' : 'กรุณาแนบรูป',
      },
    ],
  }),
  'payer.document$': createValidation({
    errorMessages: [
      {
        rule: (value, { app }) => !isPayerIdCardZeroByte(app),
        message: (value, appState) => {
          return 'เอกสาร ผู้ชำระเบี้ย สูญหาย กรุณากดปุ่ม "ดู" แล้วทำการลบ เพื่ออับโหลดเอกสารอีกครั้ง'
        },
      },
    ],
    requiredMessages: [
      {
        rule: (value, { app }) => !isPayerIdCardZeroByte(app),
        message: 'เอกสาร ผู้ชำระเบี้ย สูญหาย กรุณากดปุ่ม "ดู" แล้วทำการลบ เพื่ออับโหลดเอกสารอีกครั้ง',
      },
    ],
  }),
  'insured.document$': createValidation({
    errorMessages: [
      {
        rule: (value, { app }) => !isInsuredIdCardZeroByte(app),
        message: (value, appState) => {
          return 'เอกสาร ผู้เอาประกันภัย สูญหาย กรุณากดปุ่ม "ดู" แล้วทำการลบ เพื่ออับโหลดเอกสารอีกครั้ง'
        },
      },
    ],
    requiredMessages: [
      {
        rule: (value, { app }) => !isInsuredIdCardZeroByte(app),
        message: 'เอกสาร ผู้เอาประกันภัย สูญหาย กรุณากดปุ่ม "ดู" แล้วทำการลบ เพื่ออับโหลดเอกสารอีกครั้ง',
      },
    ],
  }),
  'banc.document$': createValidation({
    errorMessages: [
      {
        rule: (value, { app }) => !isBancSuitablityFormZeroByte(app),
        message: (value, appState) => {
          return 'เอกสาร แบบสอบถามความต้องการซื้อผลิตภัณฑ์ประกันชีวิตผ่านธนาคาร สูญหาย กรุณากดปุ่ม "ดู" แล้วทำการลบ เพื่ออับโหลดเอกสารอีกครั้ง'
        },
      },
    ],
    requiredMessages: [
      {
        rule: (value, { app }) => !isBancSuitablityFormZeroByte(app),
        message:
          'เอกสาร แบบสอบถามความต้องการซื้อผลิตภัณฑ์ประกันชีวิตผ่านธนาคาร สูญหาย กรุณากดปุ่ม "ดู" แล้วทำการลบ เพื่ออับโหลดเอกสารอีกครั้ง',
      },
    ],
  }),
  duration$: createValidation({
    requiredMessages: [
      {
        rule: flow(props(['value', 'unit']), every(isNotEmpty)),
        message: 'กรุณากรอกข้อมูล',
      },
    ],
  }),
  stopSmoking$: createValidation({
    requiredMessages: [
      {
        rule: flow(props(['month', 'year']), overEvery([every(isNotEmpty), flow(last, size, isEqual(4))])),
        message: 'กรุณากรอกข้อมูล',
      },
    ],
  }),
  email$: createValidation({
    errorMessages: [
      {
        rule: isEmail,
        message: 'รูปแบบอีเมลไม่ถูกต้อง กรุณาลองใหม่อีกครั้ง',
      },
    ],
    allow: overEvery([isLengthLessThanEqual(50)]),
  }),
  recommender$: createValidation({
    errorMessages: [
      {
        rule: (value, { app }) => {
          const category = getQuickQuoteProductCategory(app)
          const shareCaseToggleOn = getToggles().ENABLE_SHARED_AGENT_SALE_VALIDATION
          const hasActiveTamlo = hasAnyActiveLicense(AMLO_RIGHT_TYPE_CODES)(get('registrations')(value))

          if (shareCaseToggleOn) {
            const agentStatus = get('status')(value) === 'active'
            const productLicenses = getQuickQuoteSelectedProductLicenses(app)
            const selectedRiders = getSelectedRiders(app)
            const selectedRiderLicense = getRiderLicenses(selectedRiders)

            const allRequiredLicences = compact(concat(productLicenses, selectedRiderLicense))
            const allRequiredLicencesCheckList = allRequiredLicences.map((licenseCode) => {
              if (licenseCode === 'IC REFRESHER') {
                return hasActiveOrInapplicableLicense(licenseCode)
              } else {
                return hasActiveLicense(licenseCode)
              }
            })

            return (
              hasActiveTamlo &&
              agentStatus &&
              flow(get('registrations'), overEvery(allRequiredLicencesCheckList))(value)
            )
          } else {
            if (category === VALUES.INVESTMENT) {
              return (
                hasActiveTamlo &&
                flow(
                  get('registrations'),
                  overEvery([hasActiveLicense('BASIC'), hasActiveLicense('SINGLE LIC'), hasActiveLicense('OIC REG')])
                )(value)
              )
            } else {
              return hasActiveTamlo
            }
          }
        },
        message: (value, { app }) => {
          const registrations = get('registrations')(value)
          const agentStatus = get('status')(value) === 'active'

          if (!agentStatus) {
            return RECOMMENDER_MSG.AGENT_STATUS_TERMINATE
          }

          if (!hasAnyActiveLicense(AMLO_RIGHT_TYPE_CODES)(registrations)) {
            return RECOMMENDER_MSG.AMLO
          }

          if (!getToggles().ENABLE_SHARED_AGENT_SALE_VALIDATION) {
            return RECOMMENDER_MSG.AGENT_LICENSE_FALLBACK
          }

          const category = getQuickQuoteProductCategory(app)
          const productCode = getQuickQuoteProductCode(app)
          const basicPlanCode = getQuickQuoteBasicPlanCode(app)
          const selectedRiders = getSelectedRiders(app) || []

          if (category === VALUES.INVESTMENT) {
            const investmentError = getInvestmentLicenseError(registrations, productCode)
            if (investmentError) return investmentError
          }

          const basicLicenseError = getBasicProductLicenseError(registrations, basicPlanCode)
          if (basicLicenseError) return basicLicenseError

          if (!isEmpty(selectedRiders)) {
            // preserve original fallthrough: no matching rider returns the investment agent fallback
            return (
              getRiderLicenseError(registrations, basicPlanCode, selectedRiders) ??
              RECOMMENDER_MSG.AGENT_LICENSE_FALLBACK
            )
          }

          // return getBasicProductLicenseError(registrations, productCode) ?? RECOMMENDER_MSG.AGENT_LICENSE_FALLBACK
          return RECOMMENDER_MSG.AGENT_LICENSE_FALLBACK
        },
      },
    ],
  }),
  OccupationIncomePerMonth$: createValidation({
    allow: overEvery([isLengthLessThanEqual(8)]),
  }),
  'title.otherText$': createValidation({
    allow: overEvery([isLengthLessThanEqual(10)]),
  }),
  'title.text$': createValidation({
    allow: overEvery([isLengthLessThanEqual(50)]),
  }),
  'titleEN.text$': createValidation({
    allow: overEvery([isLengthLessThanEqual(9), isPersonNameEN]),
  }),
  bankBookName$: createValidation({
    allow: overEvery([isLengthLessThanEqual(60), isPersonName]),
  }),
  accountName$: createValidation({
    allow: overEvery([isLengthLessThanEqual(60), isPersonName]),
  }),
  firstName$: createValidation({
    allow: overEvery([isLengthLessThanEqual(20), isPersonName]),
  }),
  firstNameEN$: createValidation({
    allow: overEvery([isLengthLessThanEqual(20), isPersonNameEN]),
  }),
  lastName$: createValidation({
    allow: overEvery([isLengthLessThanEqual(30), isPersonName]),
  }),
  lastNameEN$: createValidation({
    allow: overEvery([isLengthLessThanEqual(50), isPersonNameEN]),
  }),
  'insured.lastName$': createValidation({
    errorMessages: [
      {
        rule: (value, { id, app }) => {
          let beneficiary = _.get(app, 'beneficiary')
          let insuredFirstName = _.get(app, 'insured.firstName')
          let insuredLastName = _.get(app, 'insured.lastName')
          if (isKid(app)) {
            beneficiary = set([0, 'firstName'], '', beneficiary)
            beneficiary = set([0, 'lastName'], '', beneficiary)
          }
          const index = _.toNumber(id.match(/[0-9]/))
          if (_.isInteger(index)) {
            const filterFname = _.filter(beneficiary, { firstName: insuredFirstName, lastName: insuredLastName })
            const isSame = _.size(filterFname)
            return isSame <= 0 ? true : false
          }
        },
        message: 'ชื่อผู้เอาประกันและผู้รับประโยชน์ซ้ำกัน กรุณาตรวจสอบและแก้ไขข้อมูลใหม่อีกครั้ง',
      },
      {
        rule: (value, { id, app }) => {
          let insuredFullName = {
            firstName: _.get(app, 'insured.firstName'),
            lastName: _.get(app, 'insured.lastName'),
          }
          const payer = get('payer', app)
          if (!_.isEmpty(payer)) {
            let payerFullName = {
              firstName: _.get(payer, 'firstName', ''),
              lastName: _.get(payer, 'lastName', ''),
            }
            return !_.isEqual(insuredFullName, payerFullName)
          }
          return true
        },
        message: 'ชื่อผู้เอาประกันและผู้ชำระเบี้ยซ้ำกัน กรุณาตรวจสอบและแก้ไขข้อมูลใหม่อีกครั้ง',
      },
    ],
  }),
  'tax.info.TIN$': createValidation({
    allow: overEvery([isLengthLessThanEqual(25), isTextAndNumberEN]),
  }),
  'tax.info.NoTIN.reason.detail$': createValidation({
    allow: overEvery([isLengthLessThanEqual(130)]),
  }),
  placeOfBirthCity: createValidation({
    allow: overEvery([isLengthLessThanEqual(50)]),
  }),
  mobileNumber$: createValidation({
    allow: isLengthLessThanEqual(10),
    errorMessages: [
      {
        rule: flow(size, isEqual(10)),
        message: 'กรุณากรอกข้อมูลให้ถูกต้อง',
      },
    ],
  }),
  'motorcycleUsage.otherText$': createValidation({
    allow: overEvery([isLengthLessThanEqual(70)]),
  }),
  sms$: createValidation({
    allow: isLengthLessThanEqual(10),
    errorMessages: [
      {
        rule: flow(size, isEqual(10)),
        message: 'กรุณากรอกข้อมูลให้ถูกต้อง',
      },
    ],
  }),
  telephoneNumber$: createValidation({
    allow: isLengthLessThanEqual(15),
    errorMessages: [
      {
        rule: flow(size, isBetweenEqual(9, 15)),
        message: 'กรุณากรอกข้อมูลให้ถูกต้อง',
      },
    ],
  }),
  'idNo.taxId$': createValidation({
    errorMessages: [
      {
        rule: (value, { app }) => isValidateTaxId(app),
        message: 'กรุณากรอกข้อมูลให้ถูกต้อง',
      },
    ],
    allow: overEvery([isLengthLessThanEqual(13)]),
  }),
  'idNo.citizenId$': createValidation({
    errorMessages: [
      {
        rule: isIDCardNo,
        message: 'กรุณากรอกข้อมูลให้ถูกต้อง',
      },
      {
        rule: (value, { app }) => !isInsuredAndPayerCitizenIdEqual(app),
        message: 'เลขประจำตัวประชาชน ของผู้เอาประกันภัยและผู้ชำระเบี้ยซ้ำกัน',
      },
    ],
    allow: overEvery([isLengthLessThanEqual(13)]),
  }),
  'idNo.govId$': createValidation({
    allow: overEvery([isLengthLessThanEqual(20)]),
  }),
  '^(?:insured|payer)\\.idNo\\.passport$': createValidation({
    errorMessages: [
      {
        rule: (value, { app }) => !isInsuredAndPayerPassportEqual(app),
        message: 'หมายเลขหนังสือเดินทาง ของผู้เอาประกันภัยและผู้ชำระเบี้ยซ้ำกัน',
      },
    ],
    infoMessages: [
      {
        rule: (value) => isEmpty(value) || (value.length > 0 && value.length < 13),
        message: 'กรณีต่างชาติไม่ถือหนังสือเดินทางบริษัทขอสงวนสิทธิ์ในการขายผ่าน Remote selling',
      },
    ],
    allow: overEvery([isLengthLessThanEqual(13), isPassport]),
  }),
  '^(?!(?:insured|payer)\\.idNo\\.passport$).+\\.idNo\\.passport$': createValidation({
    errorMessages: [
      {
        rule: (value, { app }) => !isInsuredAndPayerPassportEqual(app),
        message: 'หมายเลขหนังสือเดินทาง ของผู้เอาประกันภัยและผู้ชำระเบี้ยซ้ำกัน',
      },
    ],
    infoMessages: [
      {
        rule: (value) => isEmpty(value) || (value.length > 0 && value.length < 13),
        message: 'กรณีต่างชาติไม่ถือหนังสือเดินทางบริษัทขอสงวนสิทธิ์ในการขายผ่าน Remote selling',
      },
    ],
    allow: overEvery([isLengthLessThanEqual(13), isPassport]),
  }),
  'workplaceName[0-9]?$': createValidation({
    allow: overEvery([isLengthLessThanEqual(30)]),
  }),
  'addressNo[0-9]?$': createValidation({
    allow: overEvery([isLengthLessThanEqual(10)]),
    errorMessages: [
      {
        rule: (value, { id, app }) => {
          return /^-$|^(?=.*[0-9ก-ฮ])[0-9ก-ฮ./,()-](?:[0-9ก-ฮ./,() -]*[0-9ก-ฮ./,()-])?$/.test(value)
        },
        message: 'ข้อมูลเลขที่บ้าน ไม่ถูกต้อง',
      },
    ],
  }),
  'numberMoobanArkan[0-9]?$': createValidation({
    allow: overEvery([isLengthLessThanEqual(30)]),
    errorMessages: [
      {
        rule: (value, { id, app }) => {
          const fieldName = id.replace('numberMoobanArkan', 'addressNo')

          const addressNoData = _.get(app, fieldName)

          if (addressNoData === '-') {
            return /^(?=.*[0-9A-Za-zก-๙]).+$/.test(value ?? '')
          } else {
            return true
          }
        },
        message: 'ข้อมูลบ้านเลขที่หรือหมู่บ้าน/อาคาร ไม่ถูกต้อง',
      },
    ],
  }),
  'mhoo[0-9]?$': createValidation({
    allow: overEvery([isLengthLessThanEqual(2)]),
  }),
  'alley[0-9]?$': createValidation({
    allow: overEvery([isLengthLessThanEqual(30)]),
  }),
  'road[0-9]?$': createValidation({
    allow: overEvery([isLengthLessThanEqual(30)]),
  }),
  'postCode.value': createValidation({
    allow: overEvery([isLengthLessThanEqual(5)]),
  }),
  officeName$: createValidation({
    allow: overEvery([isLengthLessThanEqual(60)]),
  }),
  companyName$: createValidation({
    allow: overEvery([isLengthLessThanEqual(30)]),
  }),
  policyNumber$: createValidation({
    allow: overEvery([isLengthLessThanEqual(15)]),
  }),
  sumAssured$: createValidation({
    allow: overEvery([isLengthLessThanEqual(15)]),
  }),
  height$: createValidation({
    allow: overEvery([isLengthLessThanEqual(3)]),
    errorMessages: [
      {
        rule: (value, { id, app }) => {
          return isMoreThan(value, 300)
        },
        message: 'ส่วนสูงไม่ควรเกิน 300 เซนติเมตร',
      },
      {
        rule: (value, { id, app }) => {
          const weightFieldName = id.replace('height', 'weight')

          const w = _.get(app, weightFieldName)
          const h = value
          const bmi = calculateBMI(w, h)

          return inRange(8, 50, bmi) || isEqual(8, bmi) || isEqual(50, bmi)
        },
        message: 'กรุณาตรวจสอบน้ำหนัก/ส่วนสูงอีกครั้ง',
      },
    ],
  }),
  'relationshipToInsured.otherText$': createValidation({
    allow: overEvery([isLengthLessThanEqual(20)]),
  }),
  'relations.others$': createValidation({
    allow: overEvery([isLengthLessThanEqual(50)]),
  }),
  otherRelationRemark$: createValidation({
    allow: overEvery([isLengthLessThanEqual(50)]),
  }),
  allocation$: createValidation({
    allow: overEvery([isLengthLessThanEqual(3)]),
    errorMessages: [
      {
        rule: (value, { app }) =>
          every(isEqual(true))([isExceedBeneficiaryAllocation(app), flow(toFinite, negate(isEqual(0)))(value)]),
        message: 'กรุณากรอกร้อยละผลประโยชน์ใหม่',
      },
      {
        rule: (value, { app }) => isEqualBeneficiaryAllocation(app),
        message: 'กรุณากรอกร้อยละผลประโยชน์ให้ครบร้อยละ 100',
      },
    ],
  }),
  weight$: createValidation({
    allow: overEvery([isLengthLessThanEqual(3)]),
    errorMessages: [
      {
        rule: (value) => isMoreThan(value, 300),
        message: 'น้ำหนักไม่ควรเกิน 300 กิโลกรัม',
      },
      {
        rule: (value, { id, app }) => {
          const heightFieldName = id.replace('weight', 'height')

          const h = _.get(app, heightFieldName)
          const w = value
          const bmi = calculateBMI(w, h)

          return inRange(8, 50, bmi) || isEqual(8, bmi) || isEqual(50, bmi)
        },
        message: 'กรุณาตรวจสอบน้ำหนัก/ส่วนสูงอีกครั้ง',
      },
    ],
  }),
  'weightChangedRecently.value$': createValidation({
    allow: overEvery([isLengthLessThanEqual(2)]),
  }),
  'weightChangedRecently.reason$': createValidation({
    allow: overEvery([isLengthLessThanEqual(100)]),
  }),
  'weightChangedRecently.reason30Chars$': createValidation({
    allow: overEvery([isLengthLessThanEqual(30)]),
  }),
  totalFirstPremiumPayment$: createValidation({
    errorMessages: [
      {
        rule: (value, { id, app }) => {
          const key = getReceiptKey(id)
          const receiptType = get(`${key}.type`, app)

          if (receiptType === PAYMENT_TYPE.CASH) {
            return isDecimal(value)
          }

          return true
        },
        message: 'ยอดรวมต้องไม่มีจุดทศนิยม',
      },
      {
        rule: (value, { id, app }) => {
          if (value === 0) {
            return true
          }
          const key = getReceiptKey(id)
          const receiptType = get(`${key}.type`, app)

          if (receiptType === PAYMENT_TYPE.NETBANK || receiptType === PAYMENT_TYPE.CREDITCARD) {
            return isMinFirstTotalPayment(app, value)
          }
          return true
        },
        message: (value, { app }) => {
          const totalPremium = getQuickQuoteTotalPremium(app)
          const roundDownTotalPremium = getMinimumPaidPremium(totalPremium)

          return `ยอดชำระรวมต้องไม่ต่ำกว่า ${formatNumber(roundDownTotalPremium)} บาท`
        },
      },
      {
        rule: (value, { app }) => isMaxFirstTotalPayment(app, value),
        message: (value, { app }) => {
          const totalPremium = getQuickQuoteTotalPremium(app)
          const isMonthlyPayment = isQuickQuoteModelFactorMonthly(app)

          const maximumTotalPayment = getMaximumPaidPremium(totalPremium, isMonthlyPayment)
          return `ยอดชำระรวมต้องไม่เกิน ${formatNumber(maximumTotalPayment)} บาท`
        },
      },
    ],
  }),
  '^(?!expiry).*(date|Date)$': createValidation({
    errorMessages: [
      {
        rule: isFutureDate,
        message: 'กรุณากรอกข้อมูลให้ถูกต้อง',
      },
    ],
  }),
  bankBookNumber$: createValidation({
    errorMessages: [
      {
        rule: flow(size, isEqual(10)),
        message: 'กรุณากรอกข้อมูลให้ถูกต้อง',
      },
    ],
  }),
  bankBookNumberBay$: createValidation({
    errorMessages: [
      {
        rule: (value, { id, app }) => {
          if (isATPRequireBankNumber(app)) {
            return true
          }
          return isEqual(10, size(value))
        },
        message: 'กรุณากรอกข้อมูลให้ถูกต้อง',
      },
    ],
  }),
  accountNumber$: createValidation({
    errorMessages: [
      {
        rule: (value, { app, id }) => {
          if (id === 'DCA.accountNumber') {
            const bankCode = get(`DCA.bank.value`, app)
            const { length } = getBankAccountFormat(bankCode)

            return isEqual(length, size(value))
          }
          return isEqual(10, size(value))
        },
        message: 'กรุณากรอกข้อมูลให้ถูกต้อง',
      },
    ],
  }),
  'recommender.advisorCode$': createValidation({
    allow: overEvery([isLengthLessThanEqual(6)]),
  }),
  '(insured|payer).typeOfBusiness': createValidation({
    allow: overEvery([isLengthLessThanEqual(50)]),
  }),
  'otherInsuranceCompany.detail$': createValidation({
    allow: overEvery([isLengthLessThanEqual(250)]),
  }),
  'wasRejected.detail$': createValidation({
    allow: overEvery([isLengthLessThanEqual(100)]),
  }),
  'detail[[0-9]+].disease$': createValidation({
    allow: overEvery([isLengthLessThanEqual(250)]),
  }),
  '^beneficiary[[0-9]+].lastName': createValidation({
    errorMessages: [
      {
        rule: (value, { id, app }) => {
          let beneficiary = get('beneficiary', app)
          if (isKid(app)) {
            const payer = get('payer', app)
            beneficiary = set([0, 'firstName'], _.get(payer, 'firstName', ''), beneficiary)
            beneficiary = set([0, 'lastName'], _.get(payer, 'lastName', ''), beneficiary)
          }

          const index = toNumber(id.match(/[0-9]/))
          if (isInteger(index)) {
            const isSameName = isMatch({
              firstName: get([index, 'firstName'], beneficiary) || '',
              lastName: get([index, 'lastName'], beneficiary) || '',
            })
            const isSame = negate(flow(take(index), some(isSameName)))(beneficiary)
            return isSame
          }
          return true
        },
        message: 'ตรวจสอบพบชื่อผู้รับประโยชน์ซ้ำกัน กรุณาแก้ไขข้อมูลใหม่อีกครั้ง',
      },
      {
        rule: (value, { id, app }) => {
          let beneficiary = get('beneficiary', app)
          const insured = get('insured', app)
          if (isKid(app)) {
            const payer = get('payer', app)
            beneficiary = set([0, 'firstName'], _.get(payer, 'firstName', ''), beneficiary)
            beneficiary = set([0, 'lastName'], _.get(payer, 'lastName', ''), beneficiary)
          }

          if (!_.isEmpty(insured) && !_.isEmpty(beneficiary)) {
            const index = toNumber(id.match(/[0-9]/))
            if (isInteger(index)) {
              const dataBeneficiary = {
                firstName: _.get(beneficiary, `[${index}].firstName`, ''),
                lastName: _.get(beneficiary, `[${index}].lastName`, ''),
              }
              const dataInsured = {
                firstName: _.get(insured, 'firstName', ''),
                lastName: _.get(insured, 'lastName', ''),
              }

              const isMachInsureWithBene = _.isEqual(dataBeneficiary, dataInsured)

              return !isMachInsureWithBene
            }
          }
          return true
        },
        message: 'ชื่อผู้เอาประกันและผู้รับประโยชน์ซ้ำกัน กรุณาตรวจสอบและแก้ไขข้อมูลใหม่อีกครั้ง',
      },
    ],
  }),
  '^beneficiary[[0-9]+].birthdate': createValidation({
    errorMessages: [
      {
        rule: (value, { id, app }) => {
          let beneficiary = get('beneficiary', app)
          const insured = get('quickQuote.insured', app)

          const index = toNumber(id.match(/[0-9]/))
          if (isInteger(index)) {
            const dataBirtDateBeneficiary = get([index, 'birthdate'], beneficiary)
            const dataRelations = get([index, 'relations'], beneficiary)

            if (!_.isEmpty(dataBirtDateBeneficiary) && !_.isEmpty(dataRelations)) {
              const dataBirtDateInsure = get('birthdate', insured)

              const isBeneficiaryJuvinile = isConditionRelationJuvinile(dataRelations)
              const convertDateInsure = moment(dataBirtDateBeneficiary, 'DD/MM/YYYY')
              const convertDateBeneficiary = moment(dataBirtDateInsure.value, 'DD/MM/YYYY')
              if (isBeneficiaryJuvinile == true && convertDateInsure < convertDateBeneficiary) {
                return false
              }
            }
          }

          return true
        },
        message: 'อายุของผู้รับประโยชน์ ไม่สามารถมากกว่าผู้เอาประกันได้ กรุณากรอกใหม่อีกครั้ง',
      },
      {
        rule: (value, { id, app }) => {
          let beneficiary = get('beneficiary', app)
          const insured = get('quickQuote.insured', app)

          const index = toNumber(id.match(/[0-9]/))
          if (isInteger(index)) {
            const dataBirtDateBeneficiary = get([index, 'birthdate'], beneficiary)
            const dataRelations = get([index, 'relations'], beneficiary)

            if (!_.isEmpty(dataBirtDateBeneficiary) && !_.isEmpty(dataRelations)) {
              const dataBirtDateInsure = get('birthdate', insured)

              const isBeneficiaryJuvinile = isConditionRelationParants(dataRelations)
              const convertDateInsure = moment(dataBirtDateBeneficiary, 'DD/MM/YYYY')
              const convertDateBeneficiary = moment(dataBirtDateInsure.value, 'DD/MM/YYYY')
              if (isBeneficiaryJuvinile == true && convertDateInsure >= convertDateBeneficiary) {
                return false
              }
            }
          }

          return true
        },
        message: 'อายุของผู้รับประโยชน์ ไม่สามารถน้อยกว่าผู้เอาประกันได้ กรุณากรอกใหม่อีกครั้ง',
      },
    ],
  }),
  '^beneficiary[[0-9]+].relations': createValidation({
    errorMessages: [
      {
        rule: (req, { id, app }) => {
          if (_.isEmpty(req) || _.isEmpty(req.value)) {
            return false
          }
          return true
        },
        message: 'กรุณาระบุความสัมพันธ์ของผู้รับประโยชน์กับผู้เอาประกัน',
      },
    ],
    infoMessages: [
      {
        rule: (req, { id, app }) => {
          if (!_.isEmpty(req) && !_.isEmpty(req.value) && _.includes(['ESTATE'], req.value)) {
            return false
          }
          return true
        },
        message:
          'หากประสงค์ยกผลประโยชน์เป็นกองมรดก กรุณาระบุชื่อว่า "กองมรดก" และระบุนามสกุลของผู้เอาประกันในช่อง "นามสกุล',
      },
      {
        rule: (req, { id, app }) => {
          if (
            !_.isEmpty(req) &&
            !_.isEmpty(req.value) &&
            !_.includes(['SPOUSE', 'PARENT', 'CHILD', 'ESTATE', 'PARTNER'], req.value)
          ) {
            return false
          }
          return true
        },
        message:
          'ความสัมพันธ์ที่ท่านเลือกเข้าเงื่อนไขที่ต้องแนบหลักฐานพิสูจน์ความสัมพันธ์ระหว่าง ผู้ขอเอาประกันภัยและผู้รับประโยชน์ให้ท่านแนบเอกสารที่เกี่ยวข้องในขั้นตอนส่งใบคำขอเอาประกันภัย',
      },
    ],
  }),
  'payer.lastName': createValidation({
    errorMessages: [
      {
        rule: (value, { id, app }) => {
          const insured = get('insured', app)
          const payer = get('payer', app)

          if (!_.isEmpty(get('firstName', insured)) && !_.isEmpty(get('lastName', insured)) && !_.isEmpty(payer)) {
            const dataPayer = {
              firstName: get('firstName', payer),
              lastName: get('lastName', payer),
            }
            const dataInsured = {
              firstName: get('firstName', insured),
              lastName: get('lastName', insured),
            }

            const isMachInsureWithBene = _.isEqual(dataInsured, dataPayer)
            return !isMachInsureWithBene
          }
          return true
        },
        message: 'ชื่อผู้เอาประกันและผู้ชำระเบี้ยซ้ำกัน กรุณาตรวจสอบและแก้ไขข้อมูลใหม่อีกครั้ง',
      },
    ],
  }),
  approvalCode$: createValidation({
    allow: isLengthLessThanEqual(6),
    requiredMessages: [
      {
        rule: isLengthEqualTo(6),
        message: 'กรุณากรอกข้อมูลให้ถูกต้อง',
      },
    ],
  }),
  creditCardNumber$: createValidation({
    errorMessages: [
      {
        rule: isLengthEqualTo(16),
        message: 'หมายเลขบัตรไม่ถูกต้อง',
      },
    ],
  }),
  nameOnCard$: createValidation({
    errorMessages: [
      {
        rule: flow(trim, overEvery([negate(isEmpty), (value) => /^[^0-9\/]*$/gi.test(value)])),
        message: 'กรุณากรอกชื่อ-สกุลด้วย ก-ฮ / a-z เท่านั้น',
      },
    ],
  }),
  expiryDate$: createValidation({
    errorMessages: [
      {
        rule: overEvery([flow((date) => moment().isSameOrBefore(moment(date, 'MM/YY'), 'month')), isLengthEqualTo(5)]),
        message: 'บัตรของคุณหมดอายุ',
      },
    ],
  }),
  securityCode$: createValidation({
    allow: overEvery([isDigit, isLengthLessThanEqual(3)]),
    errorMessages: [
      {
        rule: isLengthEqualTo(3),
        message: 'หมายเลข CVV ไม่ถูกต้อง',
      },
    ],
  }),
  insurancePrivacyConsent$: createValidation({
    errorMessages: [
      {
        rule: (value) => get('value', value) === true,
        message: 'กรุณาให้ความยินยอมก่อนทำการสมัครเพื่อขอเอาประกันภัย',
      },
    ],
  }),
})
